// Phase H Wave 3B.3 · i18n Context + hook useTranslation.
// V12 Achromatic Strict · scope ristretto: V12AdminLayout + V12AdminDashboard
// + nuove pagine Wave 3. Pagine Phase A-G sealed restano IT only (deferred Phase I).
//
// Framework: vanilla React Context + hook. Zero deps extra.
// Persistenza: localStorage key `v12_locale`.
// Fallback automatico: locale assente/corrotto → `it` (default).
import React, {
    createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";

import it from "./translations/it.json";
import en from "./translations/en.json";

const TRANSLATIONS = { it, en };
const SUPPORTED_LOCALES = Object.freeze(["it", "en"]);
const DEFAULT_LOCALE = "it";
const STORAGE_KEY = "v12_locale";

const I18nContext = createContext({
    locale: DEFAULT_LOCALE,
    setLocale: () => {},
    t: (key) => key,
    availableLocales: SUPPORTED_LOCALES,
});

/**
 * Interpolazione semplice `{name}` → sostituzione runtime.
 * Non supporta plurale/genere (deferred Phase I se serve).
 */
function _interpolate(template, params) {
    if (!template || !params) return template;
    let out = template;
    for (const [k, v] of Object.entries(params)) {
        out = out.split(`{${k}}`).join(String(v));
    }
    return out;
}

/**
 * Risolve una chiave dot-separated contro il dict delle traduzioni.
 * Return la chiave stessa se non trovata (fail-safe · nessun throw).
 */
function _resolve(dict, key) {
    if (!dict || !key) return key;
    const parts = key.split(".");
    let cur = dict;
    for (const p of parts) {
        if (cur && typeof cur === "object" && p in cur) {
            cur = cur[p];
        } else {
            return null;
        }
    }
    return typeof cur === "string" ? cur : null;
}

export function I18nProvider({ children, initialLocale }) {
    const [locale, setLocaleState] = useState(() => {
        if (initialLocale && SUPPORTED_LOCALES.includes(initialLocale)) {
            return initialLocale;
        }
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored && SUPPORTED_LOCALES.includes(stored)) return stored;
        } catch {
            // Storage non disponibile (SSR / incognito strict) · fallback default.
        }
        return DEFAULT_LOCALE;
    });

    const setLocale = useCallback((next) => {
        if (!SUPPORTED_LOCALES.includes(next)) return;
        setLocaleState(next);
        try {
            localStorage.setItem(STORAGE_KEY, next);
        } catch {
            // Storage write failed · in-memory only.
        }
        // Aggiorna attributo lang dell'HTML per screen readers.
        try {
            document.documentElement.setAttribute("lang", next);
        } catch {
            // SSR / tests environment.
        }
    }, []);

    useEffect(() => {
        try {
            document.documentElement.setAttribute("lang", locale);
        } catch {
            // noop.
        }
    }, [locale]);

    const t = useCallback((key, params) => {
        const dict = TRANSLATIONS[locale] || TRANSLATIONS[DEFAULT_LOCALE];
        let value = _resolve(dict, key);
        if (value === null && locale !== DEFAULT_LOCALE) {
            // Fallback alla lingua default se traduzione mancante.
            value = _resolve(TRANSLATIONS[DEFAULT_LOCALE], key);
        }
        if (value === null) return key; // ultimo fallback · ritorna la chiave.
        return _interpolate(value, params);
    }, [locale]);

    const value = useMemo(() => ({
        locale, setLocale, t, availableLocales: SUPPORTED_LOCALES,
    }), [locale, setLocale, t]);

    return (
        <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
    );
}

export function useTranslation() {
    return useContext(I18nContext);
}

export { SUPPORTED_LOCALES, DEFAULT_LOCALE, STORAGE_KEY };
